import { FormEvent, useEffect, useState } from 'react';
import type { BankAccount, ConfirmedTransfer, PendingTransfer } from '@kassa/shared';
import { api, ApiError } from '../api';
import { formatDate, formatMoney } from '../format';
import { useConfirm } from '../components/ConfirmDialog';

interface Props {
  onBack: () => void;
}

// Şirkət satışlarının köçürmə ilə ödənişi (KT-55). Mühasib bank hesabını seçir, qismən və ya tam təsdiqləyir.
// Eyni təsdiq düyməsinin təkrar basılması bir ödəniş yaradır (hər sətir üçün UUID saxlanılır).
export function Transfers({ onBack }: Props) {
  const confirmDialog = useConfirm();
  const [pending, setPending] = useState<PendingTransfer[] | null>(null);
  const [confirmed, setConfirmed] = useState<ConfirmedTransfer[]>([]);
  const [reverseIds, setReverseIds] = useState<Record<string, string>>({});
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [accountId, setAccountId] = useState('');
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [payIds, setPayIds] = useState<Record<string, string>>({});
  const [newName, setNewName] = useState('');
  const [newNumber, setNewNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function reload() {
    return Promise.all([api.transfersPending(), api.transferAccounts(), api.transfersConfirmed()])
      .then(([p, a, c]) => {
        setPending(p);
        setConfirmed(c);
        setAccounts(a);
        setAccountId((cur) => cur || a[0]?.id || '');
        setAmounts(Object.fromEntries(p.map((r) => [r.docId, amounts[r.docId] ?? String(r.pendingAmount)])));
        setError(null);
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Siyahı yüklənmədi'));
  }

  useEffect(() => {
    reload();
  }, []);

  async function addAccount(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const a = await api.transferAccountCreate({ name: newName.trim(), bankAccount: newNumber.trim() || null });
      setNewName('');
      setNewNumber('');
      setAccounts((cur) => [...cur, a]);
      setAccountId(a.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Hesab yaradılmadı');
    } finally {
      setBusy(false);
    }
  }

  async function confirm(row: PendingTransfer) {
    const amount = Number(amounts[row.docId]);
    if (!accountId) return setError('Əvvəl bank hesabı seçin');
    if (!(amount > 0) || amount > row.pendingAmount) return setError(`Məbləğ 0-dan böyük və ${formatMoney(row.pendingAmount)}-dən çox olmamalıdır`);
    // Eyni düymənin təkrarı eyni UUID ilə gedir: şəbəkə qırılsa, ikinci ödəniş yaranmır
    const id = payIds[row.docId] ?? crypto.randomUUID();
    setPayIds((cur) => ({ ...cur, [row.docId]: id }));
    setBusy(true);
    setError(null);
    try {
      const r = await api.transferConfirm(row.docId, { id, moneyAccountId: accountId, amount, note: notes[row.docId]?.trim() || null });
      setNotice(`${row.displayNo}: ${formatMoney(r.amount)} təsdiqləndi — qalıq ${formatMoney(r.pendingAfter)}`);
      setPayIds((cur) => {
        const next = { ...cur };
        delete next[row.docId];
        return next;
      });
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Təsdiq olunmadı. Yenidən cəhd edin.');
    } finally {
      setBusy(false);
    }
  }

  // Ləğv: əks sətir yazılır, borc geri qayıdır. Eyni düymənin təkrarı eyni UUID ilə gedir.
  async function reverse(row: ConfirmedTransfer) {
    if (!(await confirmDialog(`«${row.displayNo}» üzrə ${formatMoney(row.amount)} köçürmə ödənişi ləğv olunsun? Borc geri qayıdacaq, sənəd silinmir.`, { confirmLabel: 'Ləğv et', danger: true }))) return;
    const id = reverseIds[row.paymentId] ?? crypto.randomUUID();
    setReverseIds((cur) => ({ ...cur, [row.paymentId]: id }));
    setBusy(true);
    setError(null);
    try {
      await api.transferReverse(row.paymentId, id);
      setNotice(`${row.displayNo}: ${formatMoney(row.amount)} ləğv olundu`);
      setReverseIds((cur) => {
        const next = { ...cur };
        delete next[row.paymentId];
        return next;
      });
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Ləğv olunmadı. Yenidən cəhd edin.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="shell">
      <header className="topbar">
        <span className="brand">Köçürmələr</span>
        <span className="topbar-spacer" />
        <button type="button" className="link-btn back-home" onClick={onBack}>
          Geri
        </button>
      </header>

      <main className="content">
        <section className="card card-wide">
          <h2>Gözlənilən köçürmələr</h2>
          <p className="muted small">Şirkət satışlarının köçürmə ilə ödənişi. Pul bank hesabına düşəndə təsdiqləyin — borc bağlanır.</p>

          <div className="form-grid">
            <label>
              Bank hesabı
              <select value={accountId} onChange={(e) => setAccountId(e.target.value)} disabled={busy || accounts.length === 0}>
                {accounts.length === 0 && <option value="">Hesab yoxdur</option>}
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                    {a.bankAccount ? ` · ${a.bankAccount}` : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <form onSubmit={addAccount} className="form-grid" aria-label="Yeni bank hesabı">
            <label>
              Yeni hesab adı
              <input value={newName} onChange={(e) => setNewName(e.target.value)} disabled={busy} />
            </label>
            <label>
              Hesab nömrəsi (istəyə görə)
              <input value={newNumber} onChange={(e) => setNewNumber(e.target.value)} disabled={busy} />
            </label>
            <button type="submit" className="btn-small" disabled={busy || newName.trim().length < 2}>
              Hesab əlavə et
            </button>
          </form>

          {notice && <p className="ok" aria-live="polite">{notice}</p>}
          {error && (
            <p className="bad" role="alert">
              {error}
            </p>
          )}
          {!pending && !error && <p className="muted">Yüklənir…</p>}
          {pending && pending.length === 0 && <p className="muted">Gözləyən köçürmə yoxdur.</p>}
          {pending && pending.length > 0 && (
            <ul className="product-list">
              {pending.map((row) => (
                <li key={row.docId} className="product-row">
                  <span className="product-name">
                    {row.customerName}
                    {row.customerVoen && <span className="muted small"> · VÖEN {row.customerVoen}</span>}
                  </span>
                  <span className="muted small">
                    {row.displayNo} · {formatDate(row.date)} · {row.waitingDays} gün gözləyir
                  </span>
                  <span className="bad">Qalıq: {formatMoney(row.pendingAmount)}</span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    aria-label={`${row.displayNo} təsdiq məbləği`}
                    value={amounts[row.docId] ?? ''}
                    onChange={(e) => setAmounts((cur) => ({ ...cur, [row.docId]: e.target.value }))}
                    disabled={busy}
                  />
                  <input
                    aria-label={`${row.displayNo} qeyd`}
                    placeholder="Qeyd"
                    maxLength={500}
                    value={notes[row.docId] ?? ''}
                    onChange={(e) => setNotes((cur) => ({ ...cur, [row.docId]: e.target.value }))}
                    disabled={busy}
                  />
                  <button type="button" className="btn-small" onClick={() => confirm(row)} disabled={busy || !accountId}>
                    Ödənildi
                  </button>
                </li>
              ))}
            </ul>
          )}

          <h3>Təsdiqlənmiş köçürmələr</h3>
          {confirmed.length === 0 && <p className="muted">Təsdiqlənmiş köçürmə yoxdur.</p>}
          {confirmed.length > 0 && (
            <ul className="product-list">
              {confirmed.map((row) => (
                <li key={row.paymentId} className="product-row">
                  <span className="product-name">{row.customerName}</span>
                  <span className="muted small">
                    {row.displayNo} · {formatDate(row.paidOn)} · {row.accountName}
                  </span>
                  <span className="ok">{formatMoney(row.amount)}</span>
                  <button type="button" className="link-btn" onClick={() => reverse(row)} disabled={busy}>
                    Ləğv et
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
